import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import ChannelService from '@avtechsoftware/ra-javascript-utilities/src/utilities/channels/ChannelService';
import ChannelKey from '@avtechsoftware/ra-javascript-utilities/src/classes/ChannelKey';
import { OperatorTypeDetails } from '@avtechsoftware/ra-javascript-utilities/src/types/OperatorTypes';
import BooleanChannel
  from '@avtechsoftware/ra-javascript-utilities/src/classes/channels/BooleanChannel/BooleanChannel';
import {
  CELSIUS_ID,
  FAHRENHEIT_ID, SPEED_FEET, SPEED_METERS,
} from '@avtechsoftware/ra-javascript-utilities/src/utilities/channels/ChannelValueRender';
import AlertNameCell from './cells/AlertNameCell';
import SourceCell from './cells/SourceCell';
import TagCell from './cells/TagCell';
import OperatorWithValueCell from './cells/OperatorWithValueCell';
import LatestReadingCell from './cells/LatestReadingCell';
import TrendCell from './cells/TrendCell';

const AlertRow = ({
  alert, isVisible, trendSamples, trendsLoading, userPreferences,
}) => {
  const {
    channelKeyObject,
    channel,
    startOperatorSymbol,
    clearOperatorSymbol,
    clearValue,
    isTriggered,
    isBooleanChannel,
  } = useMemo(() => {
    const cko = new ChannelKey(alert.channelKey.channelKey);
    const ch = ChannelService.getChannelGroupChannel(
      cko.channelGroupType,
      cko.channelIndex,
      cko.channelType,
    );
    const {
      reverseOperator,
      symbol,
      hysteresisMultiplier,
    } = OperatorTypeDetails[alert.startOperatorTypeId];
    return {
      channelKeyObject: cko,
      channel: ch,
      startOperatorSymbol: symbol,
      clearOperatorSymbol: OperatorTypeDetails[reverseOperator].symbol,
      clearValue: alert.startThreshold + (ch.hysteresisBuffer * hysteresisMultiplier),
      isTriggered: alert.triggeredTimestamp !== null,
      isBooleanChannel: ch instanceof BooleanChannel,
    };
  }, [
    alert.channelKey.channelKey,
    alert.startOperatorTypeId,
    alert.startThreshold,
    alert.triggeredTimestamp,
  ]);

  return (
    <tr>
      {isVisible('alertName') && <AlertNameCell alert={alert} isTriggered={isTriggered} />}
      {isVisible('source') && <SourceCell alert={alert} channel={channel} />}
      {isVisible('tag') && <TagCell tagNames={alert.tagNames} />}
      {isVisible('threshold') && (
        <OperatorWithValueCell
          alert={alert}
          channel={channel}
          channelKeyObject={channelKeyObject}
          operatorSymbol={startOperatorSymbol}
          value={alert.startThreshold}
          specialIsTrigger
          userPreferences={userPreferences}
        />
      )}
      {isVisible('clearValue') && (
        <OperatorWithValueCell
          alert={alert}
          channel={channel}
          channelKeyObject={channelKeyObject}
          operatorSymbol={clearOperatorSymbol}
          value={clearValue}
          specialIsTrigger={false}
          userPreferences={userPreferences}
        />
      )}
      {isVisible('latestReading') && (
        <LatestReadingCell
          alert={alert}
          channelKeyObject={channelKeyObject}
          isTriggered={isTriggered}
          userPreferences={userPreferences}
        />
      )}
      {isVisible('trend24h') && (
        <TrendCell
          samples={trendSamples}
          isLoading={trendsLoading}
          thresholdValue={alert.startThreshold}
          isTriggered={isTriggered}
          isBoolean={isBooleanChannel}
          userPreferences={userPreferences}
        />
      )}
    </tr>
  );
};

AlertRow.propTypes = {
  alert: PropTypes.shape({
    id: PropTypes.number.isRequired,
    description: PropTypes.string,
    triggeredTimestamp: PropTypes.number,
    startThreshold: PropTypes.number,
    startOperatorTypeId: PropTypes.number,
    tagNames: PropTypes.arrayOf(PropTypes.string),
    componentName: PropTypes.string,
    deviceType: PropTypes.string,
    channelKey: PropTypes.shape({
      channelKey: PropTypes.string,
      macAddress: PropTypes.string,
    }),
  }).isRequired,
  isVisible: PropTypes.func.isRequired,
  trendSamples: PropTypes.arrayOf(PropTypes.shape({
    time: PropTypes.instanceOf(Date),
    value: PropTypes.number,
  })),
  trendsLoading: PropTypes.bool,
  // eslint-disable-next-line react/forbid-prop-types
  userPreferences: PropTypes.shape({
    temperatureScale: PropTypes.oneOf([CELSIUS_ID, FAHRENHEIT_ID]),
    airSpeedScale: PropTypes.oneOf([SPEED_METERS, SPEED_FEET]),
    timezone: PropTypes.string,
  }),
};

AlertRow.defaultProps = {
  trendSamples: null,
  trendsLoading: false,
  userPreferences: PropTypes.shape({
    temperatureScale: CELSIUS_ID,
    airSpeedScale: SPEED_METERS,
    timezone: 'America/New_York',
  }),
};

const arePropsEqual = (prev, next) => (
  prev.isVisible === next.isVisible
  && prev.trendsLoading === next.trendsLoading
  && prev.trendSamples === next.trendSamples
  && prev.alert === next.alert
  && prev.userPreferences === next.userPreferences
);

export default React.memo(AlertRow, arePropsEqual);
